import { useMessageStore } from '@/stores/messageStore'
import { useRealtimeChannel } from '@/composables/useRealtimeChannel'
import type { UserMessage } from '@/stores/messageStore'

// Global state to ensure single subscription
let realtimeInstance: any = null
let isInitialized = false

export function useMessages() {
  const messageStore = useMessageStore()
  const supabase = useSupabaseClient()
  const user = useSupabaseUser()

  // Initialize real-time channel only once
  if (!realtimeInstance) {
    realtimeInstance = useRealtimeChannel(
      supabase,
      'user_messages',
      'public',
      {
        onInsert: (record: UserMessage) => {
          // Only handle messages for the current user
          if (record.user_id === user.value?.id) {
            messageStore.handleNewMessage(record)
          }
        },
        onUpdate: (newRecord: UserMessage, oldRecord: UserMessage) => {
          // Only handle messages for the current user
          if (newRecord.user_id === user.value?.id) {
            messageStore.handleMessageUpdate(newRecord)
          }
        },
        onDelete: (deletedRecord: UserMessage) => {
          // Only handle messages for the current user
          if (deletedRecord.user_id === user.value?.id) {
            messageStore.handleMessageDelete(deletedRecord)
          }
        }
      },
      false // Don't auto-unsubscribe on unmount for global instance
    )
  }

  const { subscribe, unsubscribe, isSubscribed } = realtimeInstance

  // Initialize messages and real-time sync
  const initialize = async () => {
    if (!user.value || isInitialized) return

    try {
      console.log('🚀 Initializing messages system...')
      // Fetch initial messages
      await messageStore.fetchMessages()
      
      // Start real-time sync only if not already subscribed
      if (!isSubscribed()) {
        console.log('📡 Starting realtime subscription...')
        subscribe()
      } else {
        console.log('📡 Realtime already subscribed')
      }
      
      isInitialized = true
    } catch (error) {
      console.error('Error initializing messages:', error)
    }
  }

  // Clean up real-time sync
  const cleanup = () => {
    console.log('🧹 Cleaning up messages system...')
    unsubscribe()
    isInitialized = false
  }

  // Message actions
  const markAsRead = async (messageId: string) => {
    await messageStore.markAsRead(messageId)
  }

  const archiveMessage = async (messageId: string) => {
    await messageStore.archiveMessage(messageId)
  }

  const unarchiveMessage = async (messageId: string) => {
    await messageStore.unarchiveMessage(messageId)
  }

  // Bulk actions
  const markAllAsRead = async () => {
    const unreadMessages = messageStore.unreadMessages
    const promises = unreadMessages.map(message => markAsRead(message.id))
    await Promise.all(promises)
  }

  // Auto-initialize when user is available (only run once)
  watch(user, (newUser, oldUser) => {
    if (newUser && !oldUser) {
      // Only initialize when user becomes available for the first time
      initialize()
    } else if (!newUser && oldUser) {
      // Cleanup when user logs out
      cleanup()
    }
  }, { immediate: true })

  return {
    // Store state
    messages: computed(() => messageStore.messages),
    inboxMessages: computed(() => messageStore.inboxMessages),
    archivedMessages: computed(() => messageStore.archivedMessages),
    unreadMessages: computed(() => messageStore.unreadMessages),
    unreadCount: computed(() => messageStore.unreadCount),
    isLoading: computed(() => messageStore.isLoading),
    activeTab: computed(() => messageStore.activeTab),

    // Store actions
    setActiveTab: messageStore.setActiveTab,

    // Message actions
    markAsRead,
    archiveMessage,
    unarchiveMessage,
    markAllAsRead,

    // Utility methods
    getMessageIcon: messageStore.getMessageIcon,
    getPriorityColor: messageStore.getPriorityColor,
    formatTimeAgo: messageStore.formatTimeAgo,

    // System methods
    initialize,
    cleanup,
    isSubscribed
  }
} 